import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/lib/api";
import { company, offices, phones } from "@/data/company";

function HeroContact() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-12 overflow-hidden">
      {/* Animated wave gradient */}
      <div className="absolute inset-0 z-0">
        <motion.div
          animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(144,184,73,0.25), transparent 40%), radial-gradient(circle at 80% 70%, rgba(81,95,116,0.25), transparent 45%), radial-gradient(circle at 60% 20%, rgba(198,241,122,0.25), transparent 45%)",
            backgroundSize: "200% 200%",
          }}
        />
      </div>
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
        <motion.path
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
          d="M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,120 L0,120 Z"
          fill="rgba(144,184,73,0.08)"
          stroke="rgba(144,184,73,0.3)"
          strokeWidth="1"
        />
      </svg>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10 text-center">
        <motion.span
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-label-caps text-on-tertiary-container bg-tertiary-fixed px-3 py-1 rounded-full mb-6 inline-block"
        >
          {t("contact.hero.eyebrow")}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-h1 text-[40px] md:text-h1 text-primary mb-4"
        >
          {t("contact.hero.title")}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="font-body-lg text-on-surface-variant max-w-2xl mx-auto"
        >
          {t("contact.hero.subtitle")}
        </motion.p>
      </div>
    </section>
  );
}

function DemoForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", country: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/demo`, form);
      setStatus("ok");
      setMsg(t("contact.form.success_body"));
      setForm({ name: "", email: "", company: "", role: "", country: "", message: "" });
    } catch (err) {
      setStatus("err");
      const detail = err?.response?.data?.detail;
      setMsg(Array.isArray(detail) ? detail[0]?.msg || t("contact.form.error") : detail || t("contact.form.error"));
    }
  };

  return (
    <section className="max-w-[1280px] mx-auto px-6 pb-xl">
      <div className="grid lg:grid-cols-3 gap-md">
        {/* Contact info */}
        <motion.aside
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="bg-primary-container text-white rounded-3xl p-md relative overflow-hidden"
        >
          <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-brand/15 blur-3xl" />
          <div className="relative z-10">
            <h3 className="font-h2 text-2xl mb-1">{company.brand}</h3>
            <p className="text-zinc-400 text-sm mb-1">A flagship product by {company.parent}™</p>
            <a
              href={`https://${company.domain}`}
              className="text-brand text-xs uppercase tracking-widest font-bold hover:underline inline-flex items-center gap-1"
              data-testid="contact-domain"
            >
              <span className="material-symbols-outlined text-[14px]">language</span>
              {company.domain}
            </a>

            <div className="space-y-4 mt-md">
              <a
                href={`mailto:${company.email.contact}`}
                className="flex items-start gap-3 group"
                data-testid="contact-email"
              >
                <span className="material-symbols-outlined text-brand mt-0.5">mail</span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">{t("contact.sidebar.email_label")}</div>
                  <div className="text-sm group-hover:text-brand transition-colors">{company.email.contact}</div>
                </div>
              </a>
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-brand mt-0.5">schedule</span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">{t("contact.sidebar.sla_label")}</div>
                  <div className="text-sm">{t("contact.sidebar.sla_value")}</div>
                </div>
              </div>
            </div>

            <div className="mt-lg pt-lg border-t border-zinc-800">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">{t("contact.sidebar.phone_label")}</div>
              <ul className="space-y-2" data-testid="contact-phones">
                {phones.map((p) => (
                  <li key={p.tel}>
                    <a
                      href={`tel:${p.tel}`}
                      className="flex items-center justify-between gap-3 text-sm text-zinc-200 hover:text-brand transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-brand text-[16px]">call</span>
                        {p.number}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{p.region}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-lg pt-lg border-t border-zinc-800">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">{t("contact.sidebar.deployments_label")}</div>
              <div className="flex flex-wrap gap-2">
                {["GOCEMS · Ghana", "KOCEMS · Kenya", "TOCEMS · Tanzania", "NIMACEMS · Nigeria"].map((c) => (
                  <span key={c} className="px-2.5 py-1 rounded-full bg-zinc-800 text-xs text-zinc-300">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </motion.aside>

        {/* Form */}
        <motion.form
          onSubmit={submit}
          data-testid="demo-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-2 bg-white rounded-3xl border border-outline-variant p-md md:p-lg shadow-sm"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">{t("contact.form.name")}</label>
              <input
                data-testid="demo-name"
                required
                value={form.name}
                onChange={set("name")}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-surface-container-low border border-outline-variant focus:border-brand outline-none"
                placeholder="Adaeze Okafor"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">{t("contact.form.email")}</label>
              <input
                data-testid="demo-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-surface-container-low border border-outline-variant focus:border-brand outline-none"
                placeholder="adaeze@ministry.gov"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">{t("contact.form.company")}</label>
              <input
                data-testid="demo-company"
                required
                value={form.company}
                onChange={set("company")}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-surface-container-low border border-outline-variant focus:border-brand outline-none"
                placeholder="Federal Ministry of Environment"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">{t("contact.form.role")}</label>
              <input
                data-testid="demo-role"
                value={form.role}
                onChange={set("role")}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-surface-container-low border border-outline-variant focus:border-brand outline-none"
                placeholder="Director of Compliance"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">{t("contact.form.country")}</label>
              <input
                data-testid="demo-country"
                value={form.country}
                onChange={set("country")}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-surface-container-low border border-outline-variant focus:border-brand outline-none"
                placeholder="Nigeria"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">{t("contact.form.message")}</label>
              <textarea
                data-testid="demo-message"
                rows={4}
                value={form.message}
                onChange={set("message")}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-surface-container-low border border-outline-variant focus:border-brand outline-none resize-none"
                placeholder="Tell us about your facility, sensors, or compliance objective..."
              />
            </div>
          </div>

          <div className="mt-md flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
            <p
              data-testid="demo-status"
              className={`text-sm ${status === "ok" ? "text-brand" : status === "err" ? "text-error" : "text-on-surface-variant"}`}
            >
              {status === "loading" && t("contact.form.submitting")}
              {status !== "loading" && msg}
            </p>
            <button
              data-testid="demo-submit"
              disabled={status === "loading"}
              className="px-7 py-3.5 bg-brand text-white font-manrope font-bold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {status === "loading" ? t("contact.form.submitting") : t("contact.form.submit")}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Offices() {
  const { t } = useTranslation();
  return (
    <section className="bg-surface-container-low py-xl" data-testid="offices-section">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="mb-lg">
          <span className="text-brand text-label-caps">{t("contact.offices.eyebrow")}</span>
          <h2 className="font-h2 text-h2 text-primary mt-2">
            {t("contact.offices.title")} <span className="text-gradient">{t("contact.offices.title_accent")}</span>
          </h2>
          <p className="text-on-surface-variant mt-3 max-w-2xl">
            {t("contact.offices.subtitle")}
          </p>
          <div className="w-20 h-1 bg-brand mt-sm" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
          {offices.map((o, i) => (
            <motion.div
              key={o.code}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              data-testid={`office-${o.code.toLowerCase()}`}
              className={`relative bg-white rounded-2xl border p-md transition-all hover:-translate-y-1 ${
                o.isHQ
                  ? "border-brand shadow-[0_10px_40px_-12px_rgba(144,184,73,0.4)]"
                  : "border-outline-variant hover:border-brand"
              }`}
            >
              {o.isHQ && (
                <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest bg-brand text-white px-2 py-0.5 rounded-full font-bold">
                  {t("contact.offices.hq_badge")}
                </span>
              )}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl leading-none" aria-hidden="true">{o.flag}</span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-brand font-bold">
                    {o.code} · {o.label}
                  </div>
                  <div className="font-manrope font-extrabold text-primary text-lg leading-tight">
                    {o.country}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm text-on-surface-variant">
                <span className="material-symbols-outlined text-brand text-[18px] mt-0.5 shrink-0">
                  location_on
                </span>
                <p className="leading-relaxed">{o.address}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <>
      <HeroContact />
      <DemoForm />
      <Offices />
    </>
  );
}
